import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { Modal } from '@/components/admin/Modal';
import { Notice } from '@/components/admin/Notice';
import {
  fetchMeetLinkGuidePreview,
  sendMeetLinkGuide,
  type MeetLinkGuideSendResult,
} from '@/features/events/api';

/** サクセス面談のMeetリンク案内（送信前確認 / D-364） */
export function MeetLinkGuideModal({
  open,
  eventId,
  busy,
  result,
  onSend,
  onCancel,
  onDone,
}: {
  open: boolean;
  eventId: string;
  busy: boolean;
  result?: MeetLinkGuideSendResult | null;
  onSend: () => void;
  onCancel: () => void;
  onDone: () => void;
}): ReactElement {
  const preview = useQuery({
    queryKey: ['meet-link-guide-preview', eventId],
    queryFn: () => fetchMeetLinkGuidePreview(eventId),
    enabled: open && !result,
  });

  if (result) {
    return (
      <Modal
        open={open}
        title={result.sent ? 'Meetリンク案内を送信しました' : '案内を登録しました'}
        onClose={onDone}
        wide
        footer={
          <button
            type="button"
            onClick={onDone}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
          >
            閉じる
          </button>
        }
      >
        {result.sent ? (
          <Notice kind="success" dismissible={false}>
            受講生 {result.recipient_count} 名宛にMeetリンク案内を送信しました。
          </Notice>
        ) : (
          <Notice kind="warning" dismissible={false}>
            通知は作成されましたが、送信されませんでした。
            {result.note ? `（${result.note}）` : ''}
          </Notice>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      title="Meetリンクを案内する"
      onClose={onCancel}
      wide
      footer={
        <>
          <button type="button" onClick={onCancel} disabled={busy} className="btn-secondary">
            キャンセル
          </button>
          <button
            type="button"
            onClick={onSend}
            disabled={busy || preview.isPending || preview.isError}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            {busy ? '送信中…' : '送信する'}
          </button>
        </>
      }
    >
      {preview.isPending && <p className="text-wptext-muted">読み込み中…</p>}
      {preview.isError && (
        <Notice kind="error" dismissible={false}>
          送信内容の確認に失敗しました。Meet URLが登録されているか確認してください。
        </Notice>
      )}
      {preview.data && (
        <>
          <p className="mb-3 text-wptext-muted">
            通知テンプレートの文面で、対象の受講生にMeet URLをメールで案内します。
          </p>
          <dl className="flex flex-col gap-3">
            <div>
              <dt className="text-wptext-muted">宛先</dt>
              <dd className="text-wptext-strong">
                {preview.data.recipient_names.join('、') || '（対象者なし）'}
              </dd>
            </div>
            <div>
              <dt className="text-wptext-muted">件名</dt>
              <dd className="text-wptext-strong">{preview.data.subject}</dd>
            </div>
            <div>
              <dt className="text-wptext-muted">本文</dt>
              <dd>
                <pre className="mt-1 whitespace-pre-wrap rounded-sm border border-wpborder bg-wpcanvas/60 p-3 font-sans text-wptext-strong">
                  {preview.data.body}
                </pre>
              </dd>
            </div>
          </dl>
        </>
      )}
    </Modal>
  );
}

/** 概要タブ用の入口ボタン */
export function MeetLinkGuideButton({
  eventId,
  meetUrl,
}: {
  eventId: string;
  meetUrl: string | null;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<MeetLinkGuideSendResult | null>(null);

  if (!meetUrl) {
    return (
      <p className="mt-2 text-sm text-wptext-muted">
        Meet URLが未登録のため、リンク案内は送れません。概要の「Meet URLを編集」から登録してください。
      </p>
    );
  }

  async function onSend(): Promise<void> {
    setBusy(true);
    try {
      const sent = await sendMeetLinkGuide(eventId);
      setResult(sent);
    } finally {
      setBusy(false);
    }
  }

  function onDone(): void {
    setOpen(false);
    setResult(null);
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-3 rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
      >
        Meetリンクを案内する
      </button>
      <MeetLinkGuideModal
        open={open}
        eventId={eventId}
        busy={busy}
        result={result}
        onSend={() => void onSend()}
        onCancel={() => setOpen(false)}
        onDone={onDone}
      />
    </>
  );
}
